import React, { useEffect, useState } from 'react'
import { Box, Stack, useDisclosure, Link } from '@chakra-ui/react'
import { useAlert, Button } from '@apex/component-library'
import { useFormikContext } from 'formik'
import { ACHTransferValues, PREVIOUS } from './types'
import TransferDetails from './TransferDetails'
import { SearchAccountsResponsePayload, IraConstraintsResponsePayload } from '@Types'
import { TransferCutOff } from '../../components/TransferCutOff'
import ACHPreviewModal from './ACHPreviewModal'
import IraDistributionForm from '../IraDistributionForm'
import IraContributionForm from '../IraContributionForm'
import { TransferDirection } from '../../api/useAchSubmission'
import { MessageTypes } from '../../types/MessageTypes'
import { NO_ACH_RELATIONSHIP } from '../../constants'
import TransferFrequency from '../TransferFrequency'
import { MAX_FORM_WIDTH } from '../../utils'
import TransferFees from '../TransferFees'
import { FeeTransferType, TransferFeeDescriptionType } from '../TransferFees/types'
import { useGetAchRel } from '../../api/useGetAchRel'
import { DistributionReasonInfo } from '@Types'

export interface ACHTransferFormProps {
  amountAvailable: number
  account: SearchAccountsResponsePayload
  iraConstraints: IraConstraintsResponsePayload
  hasSubmitPermission: boolean
}

const ACHTransferForm = ({ amountAvailable, account, iraConstraints, hasSubmitPermission }: ACHTransferFormProps) => {
  const { setAlert, removeAlert, alerts } = useAlert()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [noRelAlertId, setNoRelAlertId] = useState<string | null>(null)
  const hasNoRelAlert = alerts.some((alert) => alert.id === 'no-ach-rel')
  const [applicableFees, setApplicableFees] = useState<TransferFeeDescriptionType[]>([])
  const formik = useFormikContext<ACHTransferValues>()
  const { values, isValid } = formik
  const { data: achRel, isLoading: achRelIsLoading, refetch: getAchRel } = useGetAchRel(account?.accountNumber!)

  useEffect(() => {
    if (!achRel) {
      getAchRel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const noAchRelMessage = (
    <span>
      {NO_ACH_RELATIONSHIP}{' '}
      <Link href={`/accounts/#/${account.accountNumber}/ach_relationship`} color='blue.400'>
        Add ACH Relationship
      </Link>
    </span>
  )

  useEffect(() => {
    if (!achRelIsLoading && achRel?.length === 0) {
      if (!noRelAlertId) {
        const { id } = setAlert({
          id: 'no-ach-rel',
          title: 'Error',
          description: noAchRelMessage,
          isClosable: true,
          status: MessageTypes.error
        })

        setNoRelAlertId(id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achRel, setAlert, setNoRelAlertId, noRelAlertId])

  useEffect(() => {
    return () => {
      if (noRelAlertId && hasNoRelAlert) {
        removeAlert(noRelAlertId as string)
      }
    }
  }, [removeAlert, noRelAlertId, hasNoRelAlert])

  const showIraDistributionFields = (fromAccountField?: string): boolean => {
    const hasDistributionConstraints = !!iraConstraints.distributionConstraints
    const isFromApexAccount = fromAccountField === account.accountNumber
    return hasDistributionConstraints && isFromApexAccount
  }

  const showIraContributionFields = (toAccountField?: string): boolean => {
    const hasContributionContraints = !!iraConstraints.contributionConstraints
    const isToApexAccount = toAccountField === account.accountNumber
    return hasContributionContraints && isToApexAccount
  }

  let transferDirection: TransferDirection | undefined
  if (values.fromAccount === account.accountNumber) {
    transferDirection = 'withdrawals'
  } else if (values.toAccount === account.accountNumber) {
    transferDirection = 'deposits'
  }
  useEffect(() => {
    if (iraConstraints.retirementAccount && values.totalBalanceCheckbox) {
      setApplicableFees(['CLOSING'])
    } else {
      setApplicableFees([])
    }
  }, [values.totalBalanceCheckbox, iraConstraints.retirementAccount])

  const [selectedReason, setSelectedReason] = useState<DistributionReasonInfo | null>()

  const handleSelectReason = (reason: DistributionReasonInfo | null) => {
    setSelectedReason(reason)
  }

  return (
    <Box as='form' pb={4} maxW={MAX_FORM_WIDTH} w='100%'>
      <Stack spacing={6}>
        <TransferDetails
          achRels={achRel}
          account={account}
          amountAvailable={amountAvailable}
          transferDirection={transferDirection}
        />
        {showIraDistributionFields(values.fromAccount) && (
          <IraDistributionForm
            formikSetValues={formik.setValues}
            formikValues={formik.values}
            formikBlur={formik.handleBlur}
            formikTouched={formik.touched}
            setFieldValue={formik.setFieldValue}
            setTouched={formik.setTouched}
            errors={formik.errors}
            selectedReason={selectedReason}
            handleSelectReason={handleSelectReason}
            distributionReasonOptions={iraConstraints.distributionConstraints?.validDistributionReasons}
            setFieldTouched={formik.setFieldTouched}
          />
        )}

        {showIraContributionFields(values.toAccount) && (
          <IraContributionForm constraints={iraConstraints.contributionConstraints} />
        )}

        <TransferFrequency
          selectedRecurrence={values.transferRecurrence}
          selectedFrequency={values.transferFrequency}
          selectedWhenToSend={values.whenToSend}
          selectedScheduledDate={values.transferScheduledDate}
          selectedStartDate={values.transferStartDate}
          numberOfTransfers={values.numberOfTransfers}
          isTotalBalanceTransfer={values.totalBalanceCheckbox}
          isPreviousYearContribution={values.contributionYear === PREVIOUS}
        />

        <TransferFees
          accountNumber={account.accountNumber}
          applicableFees={applicableFees}
          feeTransferType={
            transferDirection === 'withdrawals' ? FeeTransferType.achWithdrawal : FeeTransferType.achDeposit
          }
        />

        <TransferCutOff pollLocalTime={true} />
        <Box>
          <Button variant='primary' onClick={onOpen} isDisabled={!isValid}>
            Next
          </Button>
        </Box>
        {transferDirection && (
          <ACHPreviewModal
            account={account}
            accountName={values.fromAccountName}
            fromAccount={values.fromAccount}
            hasSubmitPermission={hasSubmitPermission}
            transferDirection={transferDirection}
            onClose={onClose}
            isOpen={isOpen}
            iraConstraints={iraConstraints}
            amountAvailable={amountAvailable}
          />
        )}
      </Stack>
    </Box>
  )
}
export default ACHTransferForm
